import { useState, useRef } from "react";
import { useSearchParams } from "react-router";
import { useCategories } from "../api/swr/useCategories";
import { useTiers } from "../api/swr/useTiers";
import { Button } from "~/atoms/button";
import { TierModal } from "../organisms/TierModal";
import HeadingPage from "../molecules/HeadingPage";
import { CenterSpinner } from "../atoms/spinner";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { getChartData } from "../helpers/chart";
import ChartDocuments from "../organisms/ChartDocuments";
import { ChartCrossCategoryContext } from "../organisms/DocumentCard";
import { useUserContext } from "../hooks/useUserContext";
import { PlatformRole } from "../config/types";
import type { ChartData } from "../helpers/chart";
import type { CategoryType } from "../api/types";
import { ChevronRight, Filter as FilterIcon, Info } from "lucide-react";
import { DocumentMetadataSheet } from "~/molecules/DocumentMetadataSheet";
import { ManageCategoriesButton } from "~/molecules/ManageCategoriesButton";
import { TagsModal } from "~/organisms/TagsModal";
import {
  StickyHorizontalScrollbar,
  hiddenNativeScrollbarClass,
} from "~/molecules/StickyHorizontalScrollbar";
import {
  filterDocuments,
  type DateFilterKey,
  type MultiSelectFilterKey,
} from "~/helpers/documentFilters";
import { ActiveFilterChips } from "~/organisms/filters/ActiveFilterChips";
import { FiltersSidebar } from "~/organisms/filters/FiltersSidebar";
import { useDocuments } from "~/api/v2/useDocuments";
import { useDocumentFilters } from "~/hooks/useDocumentFilters";
import { Switch } from "~/atoms/switch";
import { Label } from "~/atoms/label";

const CHART_MULTI_SELECT_FILTER_KEYS = [
  "category",
  "tag",
  "owner",
  "publisher",
] as const satisfies readonly MultiSelectFilterKey[];

const CHART_DATE_FILTER_KEYS = [
  "publishedAt",
  "adoptedAt",
] as const satisfies readonly DateFilterKey[];

const Chart = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: categories } = useCategories();
  const { data: tiers } = useTiers();
  const { data: documents, mutate: mutateDocuments } = useDocuments();
  const { user } = useUserContext();
  const { t } = useTranslation();

  const canManageFramework =
    user.role === PlatformRole.superAdmin ||
    user.role === PlatformRole.coordinator;

  const [selectedId, setSelectedId] = useState<number>();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [hideCategories, setHideCategories] = useState(false);
  const [groupByGraphs, setGroupByGraphs] = useState(false);
  const [showDrafts, setShowDrafts] = useState(false);

  const chartFilters = useDocumentFilters({
    documents: documents ?? [],
    searchParams,
    setSearchParams,
    multiSelectFields: CHART_MULTI_SELECT_FILTER_KEYS,
    dateFields: CHART_DATE_FILTER_KEYS,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const documentRefsMap = useRef<Map<number, HTMLDivElement>>(new Map());

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  if (!documents || !categories || !tiers) {
    return <CenterSpinner withText />;
  }

  const preFilteredCategories =
    user.role === PlatformRole.reader || user.role === PlatformRole.contributor
      ? categories.filter((d) => user.categories?.some((ud) => ud.id === d.id))
      : categories;

  const filteredDocuments = filterDocuments(
    documents,
    chartFilters.activeFilters,
  );
  const chartDocuments = filteredDocuments.filter(
    (document) =>
      document.tier !== null &&
      (showDrafts || document.latestPublishedVersion !== null),
  );

  const mode = groupByGraphs ? "graph" : "grid";

  const selectedCategoryIds = chartFilters.getInValues("category");
  const isSelected = (category: CategoryType) =>
    selectedCategoryIds.includes(category.id.toString());
  const visibleCategories =
    selectedCategoryIds.length > 0
      ? preFilteredCategories.filter(isSelected)
      : preFilteredCategories;

  const documentsInCategory = (category: CategoryType) =>
    chartDocuments.filter((document) => document.category.id === category.id);

  const oneColumnPerCategory = visibleCategories.map((category) => ({
    category,
    data: getChartData(documentsInCategory(category), tiers, mode),
  }));

  const singleColumnWithAllDocuments = [
    { data: getChartData(chartDocuments, tiers, mode) },
  ];

  const columnGroups: { category?: CategoryType; data: ChartData }[] =
    hideCategories ? singleColumnWithAllDocuments : oneColumnPerCategory;

  const selectedDocument = selectedId
    ? chartDocuments.find((document) => document.id === selectedId)
    : undefined;

  return (
    <>
      <div className="space-y-4 px-6 py-7">
        <HeadingPage title={t(($) => $.chart.header)} />

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => setIsFiltersOpen((v) => !v)}
              className="shrink-0"
            >
              <FilterIcon />
              {t(($) => $.commons.filter.title)}
              <ChevronRight
                className={cn(
                  "transition-transform",
                  isFiltersOpen && "rotate-90",
                )}
              />
            </Button>
            <div className="flex items-center gap-1">
              <Switch
                id="group-by-graphs"
                checked={groupByGraphs}
                onCheckedChange={setGroupByGraphs}
              />
              <Label htmlFor="group-by-graphs">
                {t(($) => $.chart.groupByGraphs)}
              </Label>
            </div>
            <div className="flex items-center gap-1">
              <Switch
                id="hide-categories"
                checked={hideCategories}
                onCheckedChange={setHideCategories}
              />
              <Label htmlFor="hide-categories">
                {t(($) => $.chart.hideCategories)}
              </Label>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(user.role === PlatformRole.coordinator ||
              user.role === PlatformRole.superAdmin) && (
              <TagsModal
                trigger={
                  <Button variant="outline">
                    {t(($) => $.modal.tags.manageTags)}
                  </Button>
                }
              />
            )}
            {canManageFramework && (
              <>
                <ManageCategoriesButton />
                <TierModal
                  trigger={
                    <Button variant="outline">
                      {`${t(($) => $.commons.buttons.create)} ${t(($) => $.commons.document.tier)}`}
                    </Button>
                  }
                />
              </>
            )}
          </div>
        </div>

        <div
          className={cn("flex items-start", isFiltersOpen ? "gap-4" : "gap-0")}
        >
          <aside
            className={cn(
              "shrink-0 overflow-hidden transition-[width,opacity] duration-200",
              isFiltersOpen ? "w-80 opacity-100" : "w-0 opacity-0",
            )}
          >
            <div
              className={cn(
                "h-full w-80 origin-left transition-transform duration-200",
                isFiltersOpen
                  ? "scale-x-100"
                  : "pointer-events-none scale-x-95",
              )}
            >
              <FiltersSidebar
                filters={chartFilters}
                showDrafts={showDrafts}
                onShowDraftsChange={setShowDrafts}
              />
            </div>
          </aside>

          <div className="min-w-0 flex-1 space-y-2">
            <ActiveFilterChips filters={chartFilters} />

            <div
              ref={scrollContainerRef}
              className={`relative flex overflow-x-auto pb-7 ${hiddenNativeScrollbarClass}`}
            >
              <div className="bg-background sticky left-0 z-20 flex w-64 shrink-0 flex-col pl-7">
                <div className="h-9" />
                {tiers.map(({ id, title, description }) => (
                  <div
                    className="even:bg-accent/70 odd:bg-background flex h-44 flex-col justify-center pl-4"
                    key={id}
                  >
                    <div className="flex items-center gap-2 pr-4">
                      <h3 className="line-clamp-2 flex-1 font-medium wrap-break-word">
                        {title}
                      </h3>
                      {canManageFramework && (
                        <TierModal
                          tierId={id}
                          trigger={
                            <Button variant="ghost" size="icon">
                              <Info className="size-5 opacity-50" />
                            </Button>
                          }
                        />
                      )}
                    </div>
                    <div className="text-muted-foreground text-sm">
                      {description}
                    </div>
                  </div>
                ))}
              </div>

              <div className="relative flex pr-7" ref={containerRef}>
                <ChartCrossCategoryContext.Provider
                  value={{
                    documents: chartDocuments,
                    showCrossCategoryLinksTooltip: !hideCategories,
                  }}
                >
                  {columnGroups.map(({ category, data }) => (
                    <ChartDocuments
                      key={category?.id ?? "all"}
                      data={data}
                      tiers={tiers}
                      hideCategories={hideCategories}
                      documentRefsMap={documentRefsMap}
                      containerRef={containerRef}
                      setSelectedId={setSelectedId}
                      canManage={canManageFramework}
                      onReorder={() => void mutateDocuments()}
                      category={category}
                    />
                  ))}
                </ChartCrossCategoryContext.Provider>
              </div>
            </div>

            <StickyHorizontalScrollbar
              targetRef={scrollContainerRef}
              deps={[categories, tiers]}
              className="fixed bottom-0 z-30 h-4 overflow-y-hidden"
            />
          </div>
        </div>
      </div>

      {selectedDocument && (
        <DocumentMetadataSheet
          documentId={selectedDocument.id}
          documentVersion={
            selectedDocument.latestPublishedVersion ??
            selectedDocument.latestVersion
          }
          open={true}
          setOpen={() => setSelectedId(undefined)}
          onSave={mutateDocuments}
        />
      )}
    </>
  );
};

export default Chart;
